import React, { useEffect, useMemo, useRef, useState } from "react";
import "./CommonTable.css";

const DEFAULT_LIMIT_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGINATION = {
  totalRecords: 0,
  page: 1,
  limit: 10,
  totalPages: 0,
  from: 0,
  to: 0,
};
const DEFAULT_SORT = { sortBy: "id", sortOrder: "desc" };

const getNestedValue = (row, key) => {
  if (!key) return "";
  return String(key)
    .split(".")
    .reduce((value, part) => (value === null || value === undefined ? undefined : value[part]), row);
};

function CommonTable({
  columns = [],
  data = [],
  dataSource,
  queryParams = {},
  onDataLoaded,
  tableState = {},
  tableActions = {},
  loading = false,
  error = "",
  pagination = DEFAULT_PAGINATION,
  sort = DEFAULT_SORT,
  search = "",
  onSearchChange,
  onSortChange,
  onPageChange,
  onLimitChange,
  onRefresh,
  showSerialNo = true,
  showActions = true,
  actionsHeader = "Actions",
  renderActions,
  tableClassName = "table table-bordered table-sm",
  wrapperClassName = "table-responsive",
  className = "",
  limitOptions = DEFAULT_LIMIT_OPTIONS,
  rowsPerPageOptions,
  rowsPerPageLabel = "Rows",
  searchPlaceholder = "Search...",
  showSearch = true,
  showRefresh = false,
  showFooter = true,
  reorderableColumns = true,
  onColumnOrderChange,
  emptyMessage = "No records found",
  noSearchResultMessage = "No records found",
}) {
  const hasDataSource = Boolean(dataSource && (typeof dataSource === 'function' || typeof dataSource.get === 'function'));
  const [remoteData, setRemoteData] = useState([]);
  const [remotePagination, setRemotePagination] = useState({ ...DEFAULT_PAGINATION, limit: dataSource?.defaultParams?.limit || DEFAULT_PAGINATION.limit });
  const [remoteSort, setRemoteSort] = useState({
    sortBy: dataSource?.defaultParams?.sortBy || sort?.sortBy || DEFAULT_SORT.sortBy,
    sortOrder: dataSource?.defaultParams?.sortOrder || sort?.sortOrder || DEFAULT_SORT.sortOrder
  });
  const [remoteSearch, setRemoteSearch] = useState(search || '');
  const [remoteLoading, setRemoteLoading] = useState(false);
  const [remoteError, setRemoteError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const queryParamsKey = JSON.stringify(queryParams || {});

  const externalPagination = hasDataSource ? remotePagination : tableState.pagination || pagination;
  const externalSort = hasDataSource ? remoteSort : tableState.sort || sort;
  const externalSearch = hasDataSource ? remoteSearch : tableState.search ?? search ?? "";
  const externalOnSearchChange = tableActions.onSearchChange || onSearchChange;
  const externalOnSortChange = tableActions.onSortChange || onSortChange;
  const externalOnPageChange = tableActions.onPageChange || onPageChange;
  const externalOnLimitChange = tableActions.onLimitChange || onLimitChange;
  const resolvedOnRefresh = hasDataSource ? () => setReloadKey(key => key + 1) : tableActions.onRefresh || onRefresh;

  const isServerControlled = Boolean(
    hasDataSource ||
      tableState.pagination ||
      tableState.sort ||
      tableState.search !== undefined ||
      externalOnSearchChange ||
      externalOnSortChange ||
      externalOnPageChange ||
      externalOnLimitChange,
  );

  const [clientPage, setClientPage] = useState(1);
  const [clientLimit, setClientLimit] = useState(10);
  const [clientSort, setClientSort] = useState(DEFAULT_SORT);
  const [localSearch, setLocalSearch] = useState(externalSearch || "");
  const [pageInput, setPageInput] = useState("1");
  const [columnOrder, setColumnOrder] = useState(() => columns.map((column) => column.key));
  const [draggedColumnKey, setDraggedColumnKey] = useState(null);
  const suppressSortClickRef = useRef(false);
  const onDataLoadedRef = useRef(onDataLoaded);

  useEffect(() => {
    onDataLoadedRef.current = onDataLoaded;
  }, [onDataLoaded]);

  const resolvedLoading = hasDataSource ? remoteLoading : tableState.loading ?? loading;
  const resolvedError = hasDataSource ? remoteError : tableState.error ?? error;
  const resolvedSort = isServerControlled ? externalSort || DEFAULT_SORT : clientSort;
  const resolvedSearch = isServerControlled ? externalSearch : localSearch;

  const resolvedLimitOptions = useMemo(() => {
    const options = rowsPerPageOptions || limitOptions || DEFAULT_LIMIT_OPTIONS;
    const normalizedOptions = options
      .map((option) => parseInt(option, 10))
      .filter((option) => Number.isFinite(option) && option > 0);

    return normalizedOptions.length > 0 ? normalizedOptions : DEFAULT_LIMIT_OPTIONS;
  }, [limitOptions, rowsPerPageOptions]);

  useEffect(() => {
    if (isServerControlled) {
      setLocalSearch(externalSearch || "");
    }
  }, [externalSearch, isServerControlled]);
  useEffect(() => {
    if (!hasDataSource || typeof dataSource.subscribe !== 'function') return undefined;
    return dataSource.subscribe(() => setReloadKey(key => key + 1));
  }, [dataSource, hasDataSource]);
  useEffect(() => {
    if (!hasDataSource) return undefined;
    const timer = setTimeout(() => {
      setRemoteSearch(localSearch);
      setRemotePagination(current => ({ ...current, page: 1 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [hasDataSource, localSearch]);

  useEffect(() => {
    if (!hasDataSource) return undefined;
    let active = true;
    const load = async () => {
      setRemoteLoading(true);
      setRemoteError('');
      try {
        const request = typeof dataSource === 'function' ? dataSource : dataSource.get;
        const response = await request({
          ...JSON.parse(queryParamsKey),
          page: remotePagination.page || 1,
          limit: remotePagination.limit || 10,
          search: remoteSearch,
          sortBy: remoteSort.sortBy,
          sortOrder: remoteSort.sortOrder
        });
        if (!active) return;
        setRemoteData(Array.isArray(response?.data) ? response.data : []);
        setRemotePagination(response?.pagination || DEFAULT_PAGINATION);
        if (response?.sort) setRemoteSort(response.sort);
        onDataLoadedRef.current?.(response);
      } catch (requestError) {
        if (active) {
          setRemoteData([]);
          setRemoteError(requestError?.message || 'Unable to load records');
        }
      } finally {
        if (active) setRemoteLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [dataSource, hasDataSource, queryParamsKey, reloadKey, remotePagination.limit, remotePagination.page, remoteSearch, remoteSort.sortBy, remoteSort.sortOrder]);

  useEffect(() => {
    setColumnOrder((prevOrder) => {
      const currentKeys = columns.map((column) => column.key);
      const keptKeys = prevOrder.filter((key) => currentKeys.includes(key));
      const newKeys = currentKeys.filter((key) => !keptKeys.includes(key));
      return [...keptKeys, ...newKeys];
    });
  }, [columns]);

  useEffect(() => {
    if (!isServerControlled) {
      setClientPage(1);
    }
  }, [localSearch, isServerControlled]);

  useEffect(() => {
    if (!isServerControlled && !resolvedLimitOptions.includes(clientLimit)) {
      setClientLimit(resolvedLimitOptions[0] || 10);
    }
  }, [clientLimit, isServerControlled, resolvedLimitOptions]);

  useEffect(() => {
    if (!isServerControlled) return undefined;

    const timer = setTimeout(() => {
      if (externalOnSearchChange && localSearch !== (externalSearch || "")) {
        externalOnSearchChange(localSearch);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [externalOnSearchChange, externalSearch, isServerControlled, localSearch]);

  const visibleColumns = useMemo(() => {
    const visible = columns.filter((column) => column.visible !== false);
    const columnMap = new Map(visible.map((column) => [column.key, column]));
    const ordered = columnOrder.map((key) => columnMap.get(key)).filter(Boolean);
    const missing = visible.filter((column) => !columnOrder.includes(column.key));

    return [...ordered, ...missing];
  }, [columnOrder, columns]);

  const clientFilteredData = useMemo(() => {
    if (isServerControlled) return hasDataSource ? remoteData : data;

    const normalizedSearch = String(localSearch || "").trim().toLowerCase();
    let nextData = Array.isArray(data) ? [...data] : [];

    if (normalizedSearch) {
      const searchableColumns = visibleColumns.filter((column) => column.searchable !== false);
      nextData = nextData.filter((row) =>
        searchableColumns.some((column) => {
          const rawValue = column.searchValue
            ? column.searchValue(row)
            : getNestedValue(row, column.searchKey || column.key);
          return String(rawValue ?? "").toLowerCase().includes(normalizedSearch);
        }),
      );
    }

    const sortColumn = visibleColumns.find(
      (column) => (column.sortKey || column.key) === clientSort.sortBy,
    );

    if (sortColumn?.sortable) {
      nextData.sort((a, b) => {
        const aValue = sortColumn.sortValue
          ? sortColumn.sortValue(a)
          : getNestedValue(a, sortColumn.sortKey || sortColumn.key);
        const bValue = sortColumn.sortValue
          ? sortColumn.sortValue(b)
          : getNestedValue(b, sortColumn.sortKey || sortColumn.key);

        const aText = String(aValue ?? "").toLowerCase();
        const bText = String(bValue ?? "").toLowerCase();

        if (!Number.isNaN(Number(aText)) && !Number.isNaN(Number(bText))) {
          return clientSort.sortOrder === "asc"
            ? Number(aText) - Number(bText)
            : Number(bText) - Number(aText);
        }

        return clientSort.sortOrder === "asc"
          ? aText.localeCompare(bText)
          : bText.localeCompare(aText);
      });
    }

    return nextData;
  }, [clientSort, data, hasDataSource, isServerControlled, localSearch, remoteData, visibleColumns]);

  const clientPagination = useMemo(() => {
    const totalRecords = clientFilteredData.length;
    const totalPages = totalRecords > 0 ? Math.ceil(totalRecords / clientLimit) : 0;
    const safePage = Math.min(Math.max(clientPage, 1), totalPages || 1);
    const from = totalRecords > 0 ? (safePage - 1) * clientLimit + 1 : 0;
    const to = totalRecords > 0 ? Math.min(safePage * clientLimit, totalRecords) : 0;

    return {
      totalRecords,
      page: safePage,
      limit: clientLimit,
      totalPages,
      from,
      to,
    };
  }, [clientFilteredData.length, clientLimit, clientPage]);

  useEffect(() => {
    if (!isServerControlled && clientPagination.page !== clientPage) {
      setClientPage(clientPagination.page);
    }
  }, [clientPage, clientPagination.page, isServerControlled]);

  const resolvedPagination = isServerControlled
    ? externalPagination || DEFAULT_PAGINATION
    : clientPagination;

  const displayData = useMemo(() => {
    if (isServerControlled) return Array.isArray(hasDataSource ? remoteData : data) ? (hasDataSource ? remoteData : data) : [];
    if (!showFooter) return clientFilteredData;

    const start = (resolvedPagination.page - 1) * resolvedPagination.limit;
    return clientFilteredData.slice(start, start + resolvedPagination.limit);
  }, [clientFilteredData, data, hasDataSource, isServerControlled, remoteData, resolvedPagination.limit, resolvedPagination.page, showFooter]);

  useEffect(() => {
    setPageInput(String(resolvedPagination.page || 1));
  }, [resolvedPagination.page]);

  const totalColumns =
    visibleColumns.length + (showSerialNo ? 1 : 0) + (showActions ? 1 : 0);

  const currentPage = resolvedPagination.page || 1;
  const totalPages = resolvedPagination.totalPages || 0;
  const isFirstPage = currentPage <= 1;
  const isLastPage = totalPages <= 0 || currentPage >= totalPages;

  const handleSort = (column) => {
    if (suppressSortClickRef.current) return;
    if (!column.sortable) return;

    const columnKey = column.sortKey || column.key;
    const nextOrder =
      resolvedSort.sortBy === columnKey && resolvedSort.sortOrder === "asc" ? "desc" : "asc";

    if (hasDataSource) {
      setRemoteSort({ sortBy: columnKey, sortOrder: nextOrder });
      setRemotePagination(current => ({ ...current, page: 1 }));
    } else if (isServerControlled) {
      externalOnSortChange?.(columnKey, nextOrder);
    } else {
      setClientSort({ sortBy: columnKey, sortOrder: nextOrder });
      setClientPage(1);
    }
  };

  const handlePageChange = (page) => {
    const safePage = Math.min(Math.max(page, 1), totalPages || 1);
    if (hasDataSource) {
      setRemotePagination(current => ({ ...current, page: safePage }));
    } else if (isServerControlled) {
      externalOnPageChange?.(safePage);
    } else {
      setClientPage(safePage);
    }
  };

  const handleLimitChange = (limit) => {
    if (hasDataSource) {
      setRemotePagination(current => ({ ...current, page: 1, limit }));
    } else if (isServerControlled) {
      externalOnLimitChange?.(limit);
    } else {
      setClientLimit(limit);
      setClientPage(1);
    }
  };

  const handleColumnDragStart = (event, column) => {
    if (!reorderableColumns) return;
    setDraggedColumnKey(column.key);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", column.key);
  };

  const handleColumnDrop = (event, targetColumn) => {
    event.preventDefault();
    const sourceKey = draggedColumnKey || event.dataTransfer.getData("text/plain");
    const targetKey = targetColumn.key;

    if (!sourceKey || sourceKey === targetKey) {
      setDraggedColumnKey(null);
      return;
    }

    const nextOrder = [...columnOrder];
    const sourceIndex = nextOrder.indexOf(sourceKey);
    const targetIndex = nextOrder.indexOf(targetKey);

    if (sourceIndex === -1 || targetIndex === -1) {
      setDraggedColumnKey(null);
      return;
    }

    nextOrder.splice(sourceIndex, 1);
    nextOrder.splice(targetIndex, 0, sourceKey);
    setColumnOrder(nextOrder);
    onColumnOrderChange?.(nextOrder);
    setDraggedColumnKey(null);

    suppressSortClickRef.current = true;
    setTimeout(() => {
      suppressSortClickRef.current = false;
    }, 0);
  };

  const renderSortIcon = (column) => {
    if (!column.sortable) return null;

    const columnKey = column.sortKey || column.key;
    if (resolvedSort.sortBy !== columnKey) {
      return <i className="bi bi-arrow-down-up common-table-sort-icon muted"></i>;
    }

    return resolvedSort.sortOrder === "asc" ? (
      <i className="bi bi-arrow-up common-table-sort-icon"></i>
    ) : (
      <i className="bi bi-arrow-down common-table-sort-icon"></i>
    );
  };

  const getCellValue = (row, column, rowIndex) => {
    if (typeof column.render === "function") {
      const value = getNestedValue(row, column.key);
      return column.render.length >= 2
        ? column.render(value, row, rowIndex)
        : column.render(row, rowIndex);
    }

    const value = getNestedValue(row, column.key);
    if (value === null || value === undefined || value === "") return "-";

    return value;
  };

  const goToPageInput = () => {
    const requestedPage = parseInt(pageInput, 10);
    if (!Number.isFinite(requestedPage)) {
      setPageInput(String(currentPage));
      return;
    }

    const safePage = Math.min(Math.max(requestedPage, 1), totalPages || 1);
    setPageInput(String(safePage));

    if (safePage !== currentPage) {
      handlePageChange(safePage);
    }
  };

  const handlePageInputKeyDown = (event) => {
    if (event.key === "Enter") {
      goToPageInput();
    }
  };

  return (
    <div className={`common-table ${className}`.trim()}>
      {(showSearch || showRefresh) && (
        <div className="common-table-toolbar">
          <div className="common-table-toolbar-left">
            {showRefresh && resolvedOnRefresh && (
              <button
                type="button"
                className="btn common-table-refresh-btn"
                onClick={resolvedOnRefresh}
                disabled={resolvedLoading}
                title="Refresh"
              >
                <i className={`bi bi-arrow-clockwise me-1 ${resolvedLoading ? "common-table-spin" : ""}`}></i>
                Refresh
              </button>
            )}
          </div>

          <div className="common-table-toolbar-right">
            {showSearch && (
              <div className="common-table-search-wrap">
                <i className="bi bi-search common-table-search-icon"></i>
                <input
                  type="text"
                  className="form-control common-table-search-input"
                  placeholder={searchPlaceholder}
                  value={localSearch}
                  onChange={(event) => setLocalSearch(event.target.value)}
                />
                {localSearch && (
                  <button
                    type="button"
                    className="common-table-search-clear"
                    onClick={() => setLocalSearch("")}
                    title="Clear search"
                  >
                    <i className="bi bi-x"></i>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {resolvedError && (
        <div className="alert alert-danger mx-2" role="alert">
          {resolvedError}
        </div>
      )}

      <div className={wrapperClassName}>
        <table className={`${tableClassName} common-table-grid`}>
          <thead>
            <tr>
              {showSerialNo && <th>S.No</th>}
              {visibleColumns.map((column) => (
                <th
                  key={column.key}
                  className={`${column.className || ""} ${column.sortable ? "common-table-sortable" : ""} ${reorderableColumns ? "common-table-draggable" : ""} ${draggedColumnKey === column.key ? "common-table-dragging" : ""}`.trim()}
                  style={{
                    width: column.width,
                    textAlign: column.align,
                  }}
                  draggable={reorderableColumns}
                  onDragStart={(event) => handleColumnDragStart(event, column)}
                  onDragOver={(event) => reorderableColumns && event.preventDefault()}
                  onDragEnd={() => setDraggedColumnKey(null)}
                  onDrop={(event) => handleColumnDrop(event, column)}
                  onClick={() => handleSort(column)}
                  scope="col"
                >
                  <span className="common-table-heading">
                    {reorderableColumns && <i className="bi bi-grip-vertical common-table-drag-icon"></i>}
                    {column.label}
                    {renderSortIcon(column)}
                  </span>
                </th>
              ))}
              {showActions && <th>{actionsHeader}</th>}
            </tr>
          </thead>
          <tbody>
            {resolvedLoading ? (
              <tr>
                <td colSpan={totalColumns} className="text-center py-4">
                  <div className="spinner-border spinner-border-sm common-table-loader me-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  Loading...
                </td>
              </tr>
            ) : displayData.length > 0 ? (
              displayData.map((row, rowIndex) => (
                <tr key={row.id || rowIndex}>
                  {showSerialNo && <td>{(resolvedPagination.from || 1) + rowIndex}</td>}
                  {visibleColumns.map((column) => (
                    <td
                      key={column.key}
                      className={column.cellClassName || ""}
                      style={{ textAlign: column.align }}
                    >
                      {getCellValue(row, column, rowIndex)}
                    </td>
                  ))}
                  {showActions && (
                    <td>{typeof renderActions === "function" ? renderActions(row) : null}</td>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={totalColumns} className="text-center text-muted py-4">
                  <div className="common-table-empty">
                    <i className="bi bi-inbox"></i>
                    <span>{resolvedSearch ? noSearchResultMessage : emptyMessage}</span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showFooter && (
        <div className="common-table-footer">
          <div className="common-table-footer-left">
            {resolvedPagination.totalRecords > 0
              ? `Showing ${resolvedPagination.from} to ${resolvedPagination.to} of ${resolvedPagination.totalRecords} records`
              : "Showing 0 records"}
          </div>

          <div className="common-table-footer-center">
            <label className="common-table-limit-label" htmlFor="common-table-limit">
              {rowsPerPageLabel}
            </label>
            <select
              id="common-table-limit"
              className="form-select form-select-sm common-table-limit-select"
              value={resolvedPagination.limit}
              onChange={(event) => handleLimitChange(parseInt(event.target.value, 10))}
              disabled={resolvedLoading}
            >
              {resolvedLimitOptions.map((limitOption) => (
                <option key={limitOption} value={limitOption}>
                  {limitOption}
                </option>
              ))}
            </select>
          </div>

          <div className="common-table-footer-right">
            <button
              type="button"
              className="btn common-table-nav-btn"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={isFirstPage || resolvedLoading}
            >
              <i className="bi bi-chevron-left me-1"></i>
              Previous
            </button>

            <div className="common-table-page-jump">
              <span>Page</span>
              <input
                type="number"
                min="1"
                max={totalPages || 1}
                className="form-control common-table-page-input"
                value={pageInput}
                onChange={(event) => setPageInput(event.target.value)}
                onKeyDown={handlePageInputKeyDown}
                onBlur={goToPageInput}
                disabled={resolvedLoading || totalPages <= 0}
              />
              <span>of {totalPages || 1}</span>
            </div>

            <button
              type="button"
              className="btn common-table-nav-btn"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={isLastPage || resolvedLoading}
            >
              Next
              <i className="bi bi-chevron-right ms-1"></i>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CommonTable;